import { filterDOMProps } from '@react-aria/utils';
import type { AriaLabelingProps, DOMProps } from '@react-types/shared';
import { forwardRef } from 'react';

import { cn } from '@infinex/ui-styles';
import type { InputProps } from '@infinex/ui/components';
import { Input } from '@infinex/ui/components';

import { useAmountInput } from './useAmountInput';

export type AmountInputProps = {
  prefix?: string;
  value: string;
  onChange?: (value: string | undefined) => void;
  decimals?: number;
  minValue?: string;
  maxValue?: string;
  /**
   * If true, the input will use the raw value instead of the formatted value.
   * This is useful for receiving cards where the input value is already formatted.
   */
  useRawValue?: boolean;
  /** Overrides the default `${prefix}0` empty-state placeholder. */
  placeholder?: string;
} & Pick<
  InputProps,
  'className' | 'style' | 'autoFocus' | 'readOnly' | 'disabled'
> &
  DOMProps &
  AriaLabelingProps;

/**
 * A controlled input component for handling numeric values with decimal places.
 * This is the base component used by both Send and Swidge features.
 */
export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  (
    {
      value,
      onChange,
      decimals = 6,
      readOnly,
      disabled,
      minValue = '0',
      maxValue,
      useRawValue = false,
      className,
      placeholder,
      prefix = '',
      autoFocus,
      style,
      ...otherProps
    },
    ref
  ) => {
    const {
      displayValue,
      error,
      handleChange,
      handleBlur,
      handleKeyDown,
      handlePaste,
      setValue,
    } = useAmountInput({
      decimals,
      initialValue: value,
      minValue,
      maxValue,
      onChange,
      prefix,
    });

    // If the value is changed externally, update the display value
    if (value !== displayValue) {
      setValue(value);
    }

    let inputValue = useRawValue ? value : displayValue;
    if (inputValue) {
      // show prefix if the input is not empty
      inputValue = prefix + inputValue;
    }

    return (
      <div className="flex max-w-full min-w-0 flex-1 items-center gap-x-2">
        <Input
          aria-label="Amount input" // before spread, let consumers override
          {...filterDOMProps(otherProps, { labelable: true })}
          ref={ref}
          type="text"
          inputMode="decimal"
          placeholder={placeholder ?? `${prefix}0`}
          value={inputValue}
          autoFocus={autoFocus}
          readOnly={readOnly}
          disabled={disabled}
          className={cn(
            'h-9 min-w-0 flex-1 overflow-hidden bg-none px-0 outline-hidden',
            'text-emphasis text-right text-3xl font-semibold tabular-nums',
            'placeholder:text-secondary',
            {
              'text-disabled cursor-not-allowed': disabled,
            },
            className
          )}
          style={style}
          // Handlers
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onWheel={(e) => {
            // Prevent accidental scrolling from changing the value
            (e.target as HTMLInputElement).blur();
          }}
          // A11y
          aria-invalid={!!error}
        />
      </div>
    );
  }
);

AmountInput.displayName = 'AmountInput';
